/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, AlertCircle, Calendar, MessageCircle } from 'lucide-react';
import api from '../../lib/api';

/**
 * TeamDataQualityWidget — Vue agrégée pour le manager.
 *
 * Permet au dirigeant d'identifier d'un coup d'œil quels agents ont besoin
 * d'un rappel ou d'un accompagnement pour consigner leurs données.
 *
 * Comportement strict :
 *  - Si `total_gaps == 0`, le widget se cache COMPLÈTEMENT (zéro pollution
 *    visuelle quand l'équipe est propre).
 *  - Aucun bouton "Voir tout" / drill-down forcé : l'action managériale
 *    (appel, message Slack, debrief) reste humaine.
 */
export default function TeamDataQualityWidget() {
    const [data, setData] = useState({ agents: [], total_gaps: 0, active_agents: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const res = await api.get('/cockpit-agent/data-gaps/team');
            setData(res?.data || { agents: [], total_gaps: 0, active_agents: 0 });
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Cache complète si chargement, erreur, ou aucun gap
    if (loading || error || (data.total_gaps ?? 0) === 0) return null;

    const { agents = [], total_gaps = 0, active_agents = 0 } = data;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-2xl border border-border bg-card p-4 sm:p-5"
            data-testid="team-data-quality-widget"
        >
            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
                <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500/15 to-amber-500/5 border border-amber-500/20 shrink-0">
                    <ClipboardList className="w-4 h-4 text-amber-600 dark:text-amber-400" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                    <h3
                        className="text-sm sm:text-base font-bold text-foreground"
                        data-testid="team-dq-title"
                    >
                        Qualité de saisie · {active_agents} agent{active_agents > 1 ? 's' : ''} à accompagner
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                        {total_gaps} action{total_gaps > 1 ? 's' : ''} en attente — un rappel ou un debrief court peut suffire à débloquer leurs KPI.
                    </p>
                </div>
            </div>

            {/* Liste agents */}
            <ul className="space-y-1.5">
                {agents.map((agent, idx) => (
                    <li
                        key={agent.id || idx}
                        className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-muted/30 border border-border/40"
                        data-testid={`team-dq-agent-${idx}`}
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-[10px] font-bold text-white shrink-0">
                                {(agent.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-foreground truncate" data-testid={`team-dq-agent-name-${idx}`}>
                                    {agent.name}
                                </div>
                                <div className="flex items-center gap-2.5 mt-0.5 text-[11px] text-muted-foreground">
                                    {agent.visits_missing > 0 && (
                                        <span className="inline-flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {agent.visits_missing} visite{agent.visits_missing > 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {agent.silent_mandates > 0 && (
                                        <span className="inline-flex items-center gap-1">
                                            <MessageCircle className="w-3 h-3" />
                                            {agent.silent_mandates} mandat{agent.silent_mandates > 1 ? 's' : ''} silencieux
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-[11px] font-bold text-amber-600 dark:text-amber-400 shrink-0"
                            data-testid={`team-dq-agent-count-${idx}`}
                        >
                            <AlertCircle className="w-3 h-3" strokeWidth={2.5} />
                            {agent.total_gaps}
                        </span>
                    </li>
                ))}
            </ul>
        </motion.div>
    );
}
